import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom'; // Import createPortal
import { useWatchlist } from '../../contexts/WatchlistContext';
import './InstrumentCard.css';

// Debug mode toggle - set to false to disable all debugging logs
const DEBUG_MODE = false;

// Store instruments data globally when we see it from API responses
if (typeof window !== 'undefined') {
  if (!window.__instrumentsData) {
    window.__instrumentsData = [];
  }
  
  // Add global helper function to search for instrument properties
  window.findInstrumentDetails = function(symbol) {
    console.log('%c[INSTRUMENT-FINDER]', 'background: #8e44ad; color: white; padding: 4px;', 'Looking for', symbol);
    
    // Find in stored instruments
    const stored = window.__instrumentsData.find(i => 
      i.symbol === symbol || 
      (i.symbol && i.symbol.includes(symbol))
    );
    
    if (stored) {
      console.log('%c[FOUND-INSTRUMENT]', 'background: #27ae60; color: white; padding: 4px;', stored);
      return stored;
    }
    
    console.log('%c[NO-INSTRUMENT-FOUND]', 'background: #c0392b; color: white; padding: 4px;', 
      'Try running this in console to look for data:', 
      'window.findRawInstrumentsData()'
    );
    return null;
  };
  
  // Add helper to scan the entire page for instruments data
  window.findRawInstrumentsData = function() {
    console.log('%c[DATA-FINDER]', 'background: #3498db; color: white; padding: 4px;', 'Scanning for instruments data...');
    
    // For get-instruments-response
    let foundInResponses = false;
    
    // Function to scan objects recursively
    const scanObject = (obj, path = '') => {
      if (!obj || typeof obj !== 'object') return;
      
      // Check for get-instruments-response
      if (obj.type === 'get-instruments-response' && Array.isArray(obj.data)) {
        console.log('%c[FOUND-RESPONSE]', 'background: #27ae60; color: white; padding: 4px;', 
          `Found get-instruments-response at ${path}`, 
          obj.data.length, 'instruments'
        );
        foundInResponses = true;
        
        // Store the data
        window.__instrumentsData = obj.data;
        
        // Show sample of instruments
        console.table(obj.data.slice(0, 3));
      }
      
      // Check for instruments with characteristic properties
      if (Array.isArray(obj) && obj.length > 0) {
        // Check if first item has instrument properties
        const firstItem = obj[0];
        if (firstItem && 
            typeof firstItem === 'object' && 
            (firstItem.min_Order_Size || firstItem.contract_Size || firstItem.symbol)) {
          console.log('%c[FOUND-INSTRUMENTS-ARRAY]', 'background: #27ae60; color: white; padding: 4px;', 
            `Found instruments array at ${path}`, 
            obj.length, 'instruments'
          );
          
          // Store the data
          window.__instrumentsData = [...window.__instrumentsData, ...obj];
          
          // Show sample of instruments
          console.table(obj.slice(0, 3));
        }
      }
      
      // Recursively scan properties
      Object.keys(obj).forEach(key => {
        scanObject(obj[key], path ? `${path}.${key}` : key);
      });
    };
    
    // Start scan from various global objects
    try {
      console.log('Scanning window.__instrumentsData...');
      console.table(window.__instrumentsData.slice(0, 3));
      
      console.log('Scanning for window.__lastWsData...');
      if (window.__lastWsData) {
        scanObject(window.__lastWsData, 'window.__lastWsData');
      }
      
      console.log('Scanning for window.instrumentsCache...');
      if (window.instrumentsCache) {
        scanObject(window.instrumentsCache, 'window.instrumentsCache');
      }
      
      if (!foundInResponses) {
        console.log('%c[NO-DATA-FOUND]', 'background: #c0392b; color: white; padding: 4px;', 
          'No get-instruments-response found in common locations.'
        );
      }
    } catch (err) {
      console.error('Error during data scan:', err);
    }
    
    return window.__instrumentsData;
  };

  // Add a listener to the window object for WebSocket messages
  if (!window.__wsMessageHandlerAdded) {
    window.__wsMessageHandlerAdded = true;
    window.__lastWsData = null;
    
    // Store the original WebSocket send method
    if (window.WebSocket) {
      const originalSend = WebSocket.prototype.send;
      
      // Override the send method to intercept outgoing messages
      WebSocket.prototype.send = function(data) {
        // Call the original send method
        originalSend.apply(this, arguments);
        
        // Try to parse the message
        try {
          const message = JSON.parse(data);
          
          // Check if this is a request for instruments
          if (message.type === 'get-instruments') {
            console.log('%c[WS-REQUEST]', 'background: #3498db; color: white; padding: 2px 4px;', 
              'Instrument request detected'
            );
          }
        } catch (e) {
          // Not JSON or other error, ignore
        }
      };
      
      // Also override the set onmessage method
      const descriptor = Object.getOwnPropertyDescriptor(WebSocket.prototype, 'onmessage');
      if (descriptor && descriptor.configurable) {
        const originalSet = descriptor.set;
        
        Object.defineProperty(WebSocket.prototype, 'onmessage', {
          set: function(callback) {
            const wrappedCallback = function(event) {
              // Try to parse the message
              try {
                const message = JSON.parse(event.data);
                
                // Store last response
                window.__lastWsData = message;
                
                // Check if this is an instruments response
                if (message.type === 'get-instruments-response' && Array.isArray(message.data)) {
                  console.log('%c[WS-INSTRUMENTS-RESPONSE]', 'background: #2ecc71; color: white; padding: 2px 4px;', 
                    `Received ${message.data.length} instruments`
                  );
                  
                  // Store the data
                  window.__instrumentsData = message.data;
                }
              } catch (e) {
                // Not JSON or other error, ignore
              }
              
              // Call the original callback
              callback(event);
            };
            
            // Call the original setter
            originalSet.call(this, wrappedCallback);
          },
          get: descriptor.get,
          configurable: true
        });
      }
    }
  }
}

// Helper function for controlled logging
const debugLog = (tag, message, data) => {
  if (!DEBUG_MODE) return;
  
  const styles = 'background: #0074D9; color: white; padding: 2px 4px; border-radius: 2px;';
  console.group(`%c${tag}`, styles);
  console.log(message);
  if (data) {
    if (Array.isArray(data)) {
      console.table(data.slice(0, 3)); // Show only first 3 items
      if (data.length > 3) {
        console.log(`... and ${data.length - 3} more items`);
      }
    } else {
      console.dir(data);
    }
  }
  console.groupEnd();
};

/**
 * InstrumentCard component for displaying market instrument with watchlist functionality
 * @param {Object} props - Component props
 * @param {Object} props.instrument - Instrument data
 * @param {Function} props.onClick - Click handler for the card
 */
const InstrumentCard = ({ 
  instrument, 
  onClick 
}) => {
  const { isInWatchlist, toggleInstrument } = useWatchlist();
  const [showDetails, setShowDetails] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // State for position
  const dropdownRef = useRef(null); // Ref for the dropdown div (needed for click outside)
  const infoButtonRef = useRef(null); // Ref for the info button
  
  // Add state for watchlist button animation
  const [watchlistAnimation, setWatchlistAnimation] = useState('');
  
  // Add references and state for price change animations
  const prevBidRef = useRef(null);
  const prevAskRef = useRef(null);
  const prevPriceRef = useRef(null);
  const prevSymbolRef = useRef(null);
  
  const [bidFlashClass, setBidFlashClass] = useState('');
  const [askFlashClass, setAskFlashClass] = useState('');
  const [priceFlashClass, setPriceFlashClass] = useState('');
  const [symbolFlashClass, setSymbolFlashClass] = useState('');

  const { 
    name, 
    price, 
    bidPrice,
    askPrice,
    change, 
    originalId,
    isPlaceholder,
    actualDecimalsUsed // This is the property we need from the instrument data
  } = instrument;

  // Check if instrument is in watchlist
  const inWatchlist = isInWatchlist(originalId || instrument.id);

  // Extract symbol and description
  const parts = name.split(' ');
  const symbol = originalId ? originalId.split('.').pop() : parts[0];
  const description = parts.slice(1).join(' ') || 'Instrument';
  
  // Handle watchlist button click
  const handleWatchlistClick = async (e) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    // Set animation class based on current state
    if (inWatchlist) {
      setWatchlistAnimation('removing');
    } else {
      setWatchlistAnimation('adding');
    }
    
    // Toggle watchlist status
    await toggleInstrument(originalId || instrument.id);
    
    // Clear animation after it completes
    setTimeout(() => {
      setWatchlistAnimation('');
    }, 500);
  };

  // Add effect to track and animate symbol changes
  useEffect(() => {
    if (prevSymbolRef.current && symbol !== prevSymbolRef.current) {
      // Symbol changed, trigger animation
      setSymbolFlashClass('symbol-flash');
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setSymbolFlashClass('');
      }, 800);
      
      return () => clearTimeout(timer);
    }
    
    prevSymbolRef.current = symbol;
  }, [symbol]);
  
  // Track and animate price changes
  useEffect(() => {
    if (prevPriceRef.current !== null && price !== prevPriceRef.current) {
      // Price changed, determine direction
      if (price > prevPriceRef.current) {
        setPriceFlashClass('price-flash-up');
      } else if (price < prevPriceRef.current) {
        setPriceFlashClass('price-flash-down');
      } else {
        setPriceFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setPriceFlashClass('');
      }, 600);
      
      return () => clearTimeout(timer);
    }
    
    prevPriceRef.current = price;
  }, [price]);
  
  // Track and animate bid price changes
  useEffect(() => {
    if (prevBidRef.current !== null && bidPrice !== prevBidRef.current) {
      // Bid price changed, determine direction
      if (bidPrice > prevBidRef.current) {
        setBidFlashClass('price-flash-up');
      } else if (bidPrice < prevBidRef.current) {
        setBidFlashClass('price-flash-down');
      } else {
        setBidFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setBidFlashClass('');
      }, 600);
      
      return () => clearTimeout(timer);
    }
    
    prevBidRef.current = bidPrice;
  }, [bidPrice]);
  
  // Track and animate ask price changes
  useEffect(() => {
    if (prevAskRef.current !== null && askPrice !== prevAskRef.current) {
      // Ask price changed, determine direction
      if (askPrice > prevAskRef.current) {
        setAskFlashClass('price-flash-up');
      } else if (askPrice < prevAskRef.current) {
        setAskFlashClass('price-flash-down');
      } else {
        setAskFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setAskFlashClass('');
      }, 600);
      
      return () => clearTimeout(timer);
    }
    
    prevAskRef.current = askPrice;
  }, [askPrice]);
  
  // Format price with appropriate decimal places
  const formatPrice = (value) => {
    if (typeof value !== 'number') return 'N/A';
    
    // Determine decimal places from instrument property or use fallback
    let decimalPlacesToUse = 5; // Default fallback
    
    if (typeof instrument.decimals === 'number' || (typeof instrument.decimals === 'string' && !isNaN(instrument.decimals))) {
       decimalPlacesToUse = Number(instrument.decimals);
    } else if (typeof actualDecimalsUsed === 'number') {
      decimalPlacesToUse = actualDecimalsUsed;
    } else {
      // Dynamic fallback based on value size
      decimalPlacesToUse = value < 10 ? 5 : value < 100 ? 3 : 2;
    }
    
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlacesToUse, 
      maximumFractionDigits: decimalPlacesToUse
    });
  };
  
  // Format the price with the last two or fewer digits (pipettes) based on decimal count
  const formatPriceWithPipettes = (value) => {
    // Determine decimal places from instrument property or use fallback
    let effectiveDecimals = 5; // Default fallback
    
    if (typeof instrument.decimals === 'number' || (typeof instrument.decimals === 'string' && !isNaN(instrument.decimals))) {
       effectiveDecimals = Number(instrument.decimals);
    } else if (typeof actualDecimalsUsed === 'number') {
      effectiveDecimals = actualDecimalsUsed;
    } else {
      // Dynamic fallback based on value size
      effectiveDecimals = value < 10 ? 5 : value < 100 ? 3 : 2;
    }
    
    if (typeof value !== 'number') return { mainPart: 'N/A', pipettes: '' };
    
    const priceStr = value.toFixed(effectiveDecimals);
    
    // Use the last 2 digits for pipettes if we have at least 2 decimals,
    // otherwise use 1 digit if we have exactly 1 decimal
    const pipetteDigits = effectiveDecimals >= 2 ? 2 : effectiveDecimals === 1 ? 1 : 0;
    
    if (pipetteDigits === 0 || priceStr.length <= pipetteDigits) {
      return { mainPart: priceStr, pipettes: '' };
    }
    
    return {
      mainPart: priceStr.slice(0, -pipetteDigits), 
      pipettes: priceStr.slice(-pipetteDigits)
    };
  };
  
  // Get direction arrow based on price change
  const getPriceDirectionArrow = () => {
    if (change > 0) return '↑';
    if (change < 0) return '↓';
    return '';
  };
  
  // Color based on positive/negative change
  const changeColor = change >= 0 ? 'positive' : 'negative';

  // Extract the trading symbol (last part of originalId)
  let displaySymbol = symbol; // Default to extracted symbol from name
  if (originalId) {
    const idParts = originalId.split('.');
    if (idParts.length > 1) { // Ensure there's a dot to split by
      displaySymbol = idParts[idParts.length - 1]; // Get the last part
    }
  }
    
  // Get formatted bid, ask, and main prices
  const bidFormatted = formatPriceWithPipettes(bidPrice);
  const askFormatted = formatPriceWithPipettes(askPrice);
  const mainPriceFormatted = formatPriceWithPipettes(price);

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    if (!showDetails) return; 

    const handleClickOutside = (event) => {
      // Check dropdownRef as it now exists in the portal
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // No need to check infoButtonRef here if we stop propagation on button click
        setShowDetails(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDetails]);

  const handleInfoClick = (e) => {
    e.stopPropagation(); 
    if (infoButtonRef.current) {
      const rect = infoButtonRef.current.getBoundingClientRect();
      // Calculate dropdown width to center it better
      const dropdownWidth = 220; // Approximate width of dropdown
      
      // Position centered below instrument card, adjusting if near screen edge
      const centerX = rect.left + (rect.width / 2);
      let left = centerX - (dropdownWidth / 2);
      
      // Ensure dropdown doesn't go offscreen
      const rightEdge = left + dropdownWidth;
      if (rightEdge > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 10; // 10px padding from right edge
      }
      if (left < 10) {
        left = 10; // 10px padding from left edge
      }
      
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // Add more space between button and dropdown
        left: left + window.scrollX
      });
    }
    setShowDetails(!showDetails); 
  };

  const handleCardClick = () => {
    if (!showDetails) { // Only trigger card click if details aren't shown
        onClick(instrument);
    }
  };

  // Prepare data to handle the component lifecycle
  useEffect(() => {
    // Debug information to help diagnose property access
    // console.log('Instrument data loaded:', instrument);
    
    // See if we can find the original raw instrument data
    const findOriginalInstrument = () => {
      if (!instrument?.originalId) return;
      
      // Try to find this instrument's raw data in global window object for debugging
      if (window.__instruments && Array.isArray(window.__instruments)) {
        const rawInstrument = window.__instruments.find(i => i.symbol === instrument.originalId);
        if (rawInstrument) {
          console.log('Found raw instrument data:', rawInstrument);
        }
      }
    };
    
    findOriginalInstrument();
  }, [instrument]);

  // Add helper function to access nested properties in the instrument object
  const getNestedProperty = (obj, path) => {
    if (!obj) return undefined;
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (current[part] === undefined) return undefined;
      current = current[part];
    }
    
    return current;
  };

  // Extract raw data from instrument and save for future reference
  useEffect(() => {
    // Skip if no instrument data
    if (!instrument) return;
    
    // Try to detect and extract raw instrument data from response formats
    const extractRawData = () => {
      // Detection: API response shape with "type": "get-instruments-response" and "data" array
      // This matches the specific format shown in the example
      if (instrument.type === 'get-instruments-response' && Array.isArray(instrument.data)) {
        debugLog('INSTRUMENT-API', 'Detected API response with instrument data array', instrument.data);
        // Store all instruments data
        window.__instrumentsData = instrument.data;
        return;
      }
      
      // Store individual instrument when its format matches API data
      if (instrument.symbol && 
          (instrument.min_Order_Size || instrument.contract_Size || instrument.decimals)) {
        // This is a raw instrument, add/update it in our global store
        const existingIndex = window.__instrumentsData.findIndex(i => i.symbol === instrument.symbol);
        
        if (existingIndex >= 0) {
          window.__instrumentsData[existingIndex] = instrument;
        } else {
          window.__instrumentsData.push(instrument);
        }
      }
      
      // Check if this instrument is a formatted version of a raw instrument
      if (instrument.originalId) {
        const rawInstrument = window.__instrumentsData.find(i => i.symbol === instrument.originalId);
        if (rawInstrument) {
          debugLog('INSTRUMENT-DETAIL', 'Found matching raw instrument data', rawInstrument);
          // Store reference to raw data in the instrument itself
          instrument.rawApiData = rawInstrument;
        }
      }
    };
    
    extractRawData();
  }, [instrument]);

  return (
    <>
      <div 
        className={`instrument-card ${changeColor} ${isPlaceholder ? 'placeholder-data' : ''} ${showDetails ? 'details-open' : ''}`} 
        onClick={handleCardClick}
      >
        {/* Inner glow effect */}
        <div className="card-inner-glow"></div>
        
        {/* Info Area Container */}
        <div className="info-area">
          {/* Watchlist Button */}
          <button 
            className={`watchlist-button ${inWatchlist ? 'in-watchlist' : ''} ${watchlistAnimation}`}
            onClick={handleWatchlistClick}
            title={inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
          >
            {inWatchlist ? '★' : '☆'}
          </button>

          {/* Info Button */}
          <button ref={infoButtonRef} className="info-button" onClick={handleInfoClick}>ⓘ</button>

          {/* Conditionally render the Details Dropdown via Portal */}
          {showDetails && createPortal(
            <div 
              ref={dropdownRef} 
              className="details-dropdown" 
              style={{ 
                position: 'absolute', // Use absolute positioning in the body
                top: `${dropdownPosition.top}px`, 
                left: `${dropdownPosition.left}px`,
                zIndex: 1000,
                backgroundColor: 'rgba(42, 42, 42, 0.95)',
                border: '1px solid #444',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                minWidth: '200px',
                maxWidth: '300px'
              }}
              onClick={(e) => e.stopPropagation()} // Prevent clicks inside dropdown closing it immediately
            >
              <h4>Instrument Details</h4>
              {
                (() => {
                  // Log the raw instrument data to console
                  debugLog('DROPDOWN-DATA', 'Instrument data for dropdown', instrument);
                  
                  // Extract symbol for display and lookup
                  let fullSymbol = instrument?.originalId || instrument?.symbol || '';
                  let displaySymbol = 'N/A';
                  
                  if (fullSymbol) {
                    const parts = fullSymbol.split('.');
                    displaySymbol = parts[parts.length - 1];
                  }
                  
                  // Direct lookup in the websocket data we've captured
                  let rawInstrumentData = null;
                  
                  // IMPORTANT: Using the exact format shown in the image
                  if (window.__instrumentsData && window.__instrumentsData.length > 0) {
                    // Try exact match first
                    rawInstrumentData = window.__instrumentsData.find(
                      item => item.symbol === fullSymbol
                    );
                    
                    // If not found, try partial match (e.g., matching just 'AUDCAD' part)
                    if (!rawInstrumentData && displaySymbol !== 'N/A') {
                      rawInstrumentData = window.__instrumentsData.find(
                        item => item.symbol && item.symbol.includes(displaySymbol)
                      );
                    }
                  }
                  
                  // Log what we found for debugging
                  console.log('%c[INSTRUMENT-LOOKUP]', 'background: #f39c12; color: white; padding: 2px 4px;',
                    `Looking for: ${fullSymbol} / ${displaySymbol}`,
                    rawInstrumentData ? 'FOUND!' : 'Not found in cached data'
                  );
                  
                  if (rawInstrumentData) {
                    console.log('%c[RAW-DATA]', 'background: #27ae60; color: white; padding: 2px 4px;', rawInstrumentData);
                  }
                  
                  // Create details using either the found raw data or fallbacks
                  const details = {
                    Symbol: displaySymbol,
                    Description: rawInstrumentData?.description || instrument.description || 'N/A',
                    'Contract Size': rawInstrumentData?.contract_Size || instrument.contract_Size || 'N/A',
                    'Min Order Size': (() => {
                      const minOrderSize = rawInstrumentData?.min_Order_Size || instrument.min_Order_Size;
                      const contractSize = rawInstrumentData?.contract_Size || instrument.contract_Size;
                      
                      if (minOrderSize && contractSize && !isNaN(minOrderSize) && !isNaN(contractSize)) {
                        const calculatedSize = parseFloat(minOrderSize) * parseFloat(contractSize);
                        return calculatedSize.toLocaleString();
                      }
                      return 'N/A';
                    })(),
                    'Max Order Size': (() => {
                      const maxOrderSize = rawInstrumentData?.max_Order_Size || instrument.max_Order_Size;
                      const contractSize = rawInstrumentData?.contract_Size || instrument.contract_Size;
                      
                      if (maxOrderSize && contractSize && !isNaN(maxOrderSize) && !isNaN(contractSize)) {
                        const calculatedSize = parseFloat(maxOrderSize) * parseFloat(contractSize);
                        return calculatedSize.toLocaleString();
                      }
                      return 'N/A';
                    })()
                  };
                  
                  return Object.entries(details).map(([key, value]) => (
                    <div className="dropdown-detail-item" key={key}>
                      <span className="dropdown-detail-key">{key}:</span>
                      <span className="dropdown-detail-value">{value}</span>
                    </div>
                  ));
                })()
              }
            </div>,
            document.body // Render into the body
          )}
        </div>

        <div className={`instrument-symbol ${symbolFlashClass}`}>{displaySymbol}</div>
        <div className="instrument-description">{description}</div>
        
        <div className="price-section">
          <div className={`main-price ${priceFlashClass}`}>
            <span className="main-price-main-part">{mainPriceFormatted.mainPart}</span><span className="main-price-pipettes">{mainPriceFormatted.pipettes}</span>
          </div>
          <div className={`change-value ${changeColor}`}>
            {getPriceDirectionArrow()}{Math.abs(change).toFixed(2)}%
          </div>
        </div>
        
        <div className="bid-ask-container">
          <div className="bid-price" data-label="BID">
            <div className={`price-value-wrapper ${bidFlashClass}`}>
              <span className="price-main-part">{bidFormatted.mainPart}</span><span className="price-pipettes">{bidFormatted.pipettes}</span>
            </div>
          </div>
          
          <div className="ask-price" data-label="ASK">
            <div className={`price-value-wrapper ${askFlashClass}`}>
              <span className="price-main-part">{askFormatted.mainPart}</span><span className="price-pipettes">{askFormatted.pipettes}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstrumentCard; 